<template>
  <section id="home" class="hero">
    <!-- Animated background particles -->
    <div class="particles">
      <span
        v-for="n in 20"
        :key="n"
        class="particle"
        :style="particleStyle(n)"
      />
    </div>

    <!-- Grid overlay -->
    <div class="grid-overlay" />

    <div class="hero-content">
      <div class="badge">
        <span class="badge-dot" />
        وكالة رقمية متكاملة
      </div>

      <h1 class="hero-title">
        نبني
        <span class="text-gold"> مستقبلك الرقمي</span>
        <br />بأيدٍ خبيرة
      </h1>

      <p class="hero-desc">
        حلول برمجية متطورة وتصاميم جرافيك احترافية تُحوّل أفكارك إلى تجارب رقمية
        لا تُنسى — من الرؤية إلى الواقع.
      </p>

      <div class="hero-btns">
        <a href="#services" class="btn-primary">اكتشف خدماتنا</a>
        <a href="#portfolio" class="btn-outline">شاهد أعمالنا</a>
      </div>

      <!-- Stats -->
      <div class="hero-stats">
        <div class="stat" v-for="s in stats" :key="s.label">
          <span class="stat-num">{{ s.num }}</span>
          <span class="stat-label">{{ s.label }}</span>
        </div>
      </div>
    </div>

    <!-- Floating visual element -->
    <div class="hero-visual">
      <div class="orb orb-1" />
      <div class="orb orb-2" />
      <div class="hero-card">
        <div class="card-icon">🚀</div>
        <div class="card-text">
          <strong>مشاريع ناجحة</strong>
          <span>+200 عميل راضٍ</span>
        </div>
      </div>
      <div class="hero-card card-2">
        <div class="card-icon">✨</div>
        <div class="card-text">
          <strong>جودة عالية</strong>
          <span>ضمان التسليم في الموعد</span>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="scroll-indicator">
      <div class="scroll-dot" />
    </div>
  </section>
</template>

<script setup>
const stats = [
  { num: "+200", label: "مشروع منجز" },
  { num: "+5", label: "سنوات خبرة" },
  { num: "98%", label: "رضا العملاء" },
  { num: "24/7", label: "دعم فني" },
];

function particleStyle(n) {
  const size = Math.random() * 4 + 1;
  return {
    width: size + "px",
    height: size + "px",
    left: ((n * 5.2) % 100) + "%",
    top: ((n * 7.3) % 100) + "%",
    animationDelay: n * 0.4 + "s",
    animationDuration: 8 + (n % 6) + "s",
  };
}
</script>

<style scoped>
.hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8rem 6vw 4rem;
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      ellipse 80% 60% at 20% 50%,
      rgba(10, 22, 40, 0.9) 0%,
      transparent 70%
    ),
    radial-gradient(
      ellipse 60% 80% at 80% 20%,
      rgba(212, 175, 55, 0.07) 0%,
      transparent 60%
    ),
    var(--bg-dark);
  gap: 2rem;
}

/* Particles */
.particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.particle {
  position: absolute;
  border-radius: 50%;
  background: var(--gold);
  opacity: 0.15;
  animation: float linear infinite;
}
@keyframes float {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0.1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    transform: translateY(-80px) scale(0.5);
    opacity: 0;
  }
}

/* Grid */
.grid-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(
      rgba(212, 175, 55, 0.04) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(212, 175, 55, 0.04) 1px, transparent 1px);
  background-size: 60px 60px;
}

/* Content */
.hero-content {
  flex: 1;
  max-width: 620px;
  z-index: 2;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid var(--border);
  color: var(--gold-light);
  padding: 0.4rem 1rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  animation: fadeInDown 0.8s ease both;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gold);
  box-shadow: 0 0 8px var(--gold);
  animation: pulse 2s ease infinite;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 8px var(--gold);
  }
  50% {
    box-shadow: 0 0 20px var(--gold);
  }
}

.hero-title {
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.25;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.8s 0.2s ease both;
}

.text-gold {
  background: linear-gradient(135deg, var(--gold-light), var(--gold));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-desc {
  color: var(--gray);
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2.5rem;
  animation: fadeInUp 0.8s 0.4s ease both;
}

.hero-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 3rem;
  animation: fadeInUp 0.8s 0.6s ease both;
}

.btn-primary {
  background: linear-gradient(135deg, var(--gold), var(--gold-dark));
  color: #000;
  font-weight: 700;
  padding: 0.85rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 6px 30px var(--gold-glow);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(212, 175, 55, 0.5);
}

.btn-outline {
  border: 1px solid var(--gold);
  color: var(--gold);
  font-weight: 600;
  padding: 0.85rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s;
}

.btn-outline:hover {
  background: var(--gold-glow);
  transform: translateY(-3px);
}

/* Stats */
.hero-stats {
  display: flex;
  gap: 2rem;
  animation: fadeInUp 0.8s 0.8s ease both;
}

.stat {
  text-align: center;
}
.stat-num {
  display: block;
  font-size: 1.8rem;
  font-weight: 900;
  background: linear-gradient(135deg, var(--gold-light), var(--gold));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.stat-label {
  font-size: 0.8rem;
  color: var(--gray);
}

/* Visual */
.hero-visual {
  flex: 1;
  max-width: 480px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  z-index: 2;
  animation: fadeInRight 1s 0.4s ease both;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
}

.orb-1 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.2), transparent);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: breathe 6s ease-in-out infinite;
}

.orb-2 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(10, 22, 40, 0.8), transparent);
  top: 20%;
  right: 10%;
}

@keyframes breathe {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.15);
  }
}

.hero-card {
  position: absolute;
  background: rgba(13, 31, 60, 0.9);
  border: 1px solid var(--border);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 1rem 1.4rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  animation: float-card 5s ease-in-out infinite;
}

.hero-card:first-of-type {
  bottom: 20%;
  left: -10%;
}
.card-2 {
  top: 15%;
  right: -5%;
  animation-delay: -2.5s;
}

@keyframes float-card {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-12px);
  }
}

.card-icon {
  font-size: 1.8rem;
}

.card-text {
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
}

.card-text strong {
  color: var(--gold);
  font-weight: 700;
}
.card-text span {
  color: var(--gray);
}

/* Scroll indicator */
.scroll-indicator {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  width: 26px;
  height: 42px;
  border: 2px solid var(--border);
  border-radius: 13px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 6px;
}

.scroll-dot {
  width: 4px;
  height: 10px;
  background: var(--gold);
  border-radius: 2px;
  animation: scroll-anim 2s ease infinite;
}

@keyframes scroll-anim {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(16px);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 900px) {
  .hero {
    flex-direction: column;
    padding: 7rem 2rem 4rem;
    text-align: center;
  }
  .hero-btns {
    justify-content: center;
  }
  .hero-stats {
    justify-content: center;
  }
  .hero-visual {
    min-height: 450px;
    width: 100%;
  }
  .hero-card:first-of-type {
    left: 0;
  }
}
</style>
